import { Link, useParams } from 'react-router-dom';
import { PhoneIcon, WhatsAppIcon } from './ContactIcons.jsx';
import { clinic, doctors, getDoctor } from '../data/site.js';

export default function DoctorPage() {
  const { slug } = useParams();
  const doctor = getDoctor(slug);
  const otherDoctors = doctors.filter((item) => item.slug !== slug);

  if (!doctor) {
    return (
      <section className="doctor-page-hero">
        <div className="container">
          <h1 className="heading-display">Doctor profile not found.</h1>
          <p className="text-body">The doctor profile you requested could not be found.</p>
          <Link to="/#dentist" className="btn btn--primary">Meet the dentists</Link>
        </div>
      </section>
    );
  }

  const heroStyle = {
    '--doctor-hero-image': `url("${doctor.heroImage}")`,
    '--doctor-hero-cutout': `url("${doctor.heroCutout || doctor.heroImage}")`
  };

  return (
    <article className="doctor-page">
      <header className="doctor-page-hero" style={heroStyle}>
        <div className="container doctor-page-hero__inner">
          <div className="doctor-page-hero__content">
            <Link to="/#dentist" className="caption doctor-page-hero__back">Clinical team</Link>
            <p className="doctor-page-hero__role">{doctor.role}</p>
            <h1 className="heading-display">{doctor.name}</h1>
            <p className="doctor-page-hero__experience">{doctor.experience}</p>
            <p className="doctor-page-hero__qualification">{doctor.qualification}</p>
            <p className="text-body">{doctor.summary}</p>
            <div className="doctor-page-hero__actions">
              <Link to="/#booking" className="btn btn--primary">Book an appointment</Link>
              <a href={clinic.phoneHref} className="btn btn--secondary btn--with-icon" aria-label={`Call ${clinic.phoneDisplay}`}>
                <PhoneIcon />
                Call Now
              </a>
              <a href={clinic.whatsappHref} className="doctor-page-hero__whatsapp" target="_blank" rel="noreferrer">
                <WhatsAppIcon />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </header>

      <section className="doctor-details">
        <div className="container doctor-details__grid">
          <div className="doctor-details__main">
            <p className="caption">About the doctor</p>
            <h2 className="heading-2">Clear, careful care from consultation to treatment planning.</h2>
            <p className="text-body">{doctor.bio}</p>

            <div className="doctor-details__cards">
              <section className="doctor-info-card">
                <h3>Areas of focus</h3>
                <ul>
                  {doctor.focus.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
              <section className="doctor-info-card">
                <h3>Helpful for patients with</h3>
                <ul>
                  {doctor.helpfulFor.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>

          <aside className="doctor-credentials" aria-label={`${doctor.name} credentials`}>
            <p className="caption">Professional background</p>
            <h2>{doctor.qualification}</h2>
            <p className="doctor-credentials__registration">Registration No. {doctor.registrationNumber}</p>
            <ul>
              {doctor.credentials.map((credential) => (
                <li key={credential}>{credential}</li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="doctor-approach">
        <div className="container doctor-approach__inner">
          <div>
            <p className="caption">Care approach</p>
            <h2 className="heading-2">What patients can expect</h2>
          </div>
          <div className="doctor-approach__steps">
            {doctor.careApproach.map((item, index) => (
              <div className="doctor-step" key={item}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="doctor-cta">
        <div className="container doctor-cta__inner">
          <div>
            <p className="caption">Plan a visit</p>
            <h2 className="heading-2">Book a consultation at My Dentist in {clinic.locality}.</h2>
            <p className="text-body">Treatment suitability depends on an in-person dental assessment, oral health, medical history and your goals.</p>
          </div>
          <Link to="/#booking" className="btn btn--primary">Book online</Link>
        </div>
      </section>

      {otherDoctors.length ? (
        <section className="doctor-related" aria-label="Other dentist">
          <div className="container">
            <p className="caption">Also at the clinic</p>
            {otherDoctors.map((item) => (
              <Link key={item.slug} to={`/dentists/${item.slug}/`} className="doctor-related__link">
                <span>{item.name}</span>
                <span>{item.role}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
